import { useState } from "react";

import { Decimal } from "decimal.js";
import { twMerge } from "tailwind-merge";
import { formatUnits } from "viem";
import { useAccount } from "wagmi";

import { useDelegateMutation } from "shared/api/cosmos/use-delegate-mutation";
import { useDelegationsQuery } from "shared/api/cosmos/use-delegations-query";
import { useUndelegateMutation } from "shared/api/cosmos/use-undelegate-mutation";
import { Validator } from "shared/api/cosmos/use-validators-query";
import { Button } from "shared/ui/button";
import { catchError } from "shared/ui/toast";
import { formatCurrency } from "shared/web3/utils";

import { useStaking } from "../providers/staking-provider";
import { Input } from "./input";
import { SelectValidator } from "./select-validator";

const tabs = ["Stake", "Unstake", "Claim"] as const;
type TabType = (typeof tabs)[number];

type Props = {
  className?: string;
  onSelect: (validator: Validator) => void;
};

export function StakingTerminal({ className, onSelect }: Props) {
  const { validator } = useStaking();
  const { address } = useAccount();

  const query = useDelegationsQuery({ address });

  const delegation = query.data?.delegation_responses?.find(
    (delegationResponse) =>
      delegationResponse.delegation?.validator_address ===
      validator?.operator_address
  );

  const commission =
    parseFloat(validator?.commission?.commission_rates?.rate ?? "0") * 100;

  const formattedBalance = delegation?.balance?.amount
    ? formatCurrency(formatUnits(BigInt(delegation.balance.amount), 18))
    : "0";

  const [activeTab, setActiveTab] = useState<TabType>("Stake");
  const [amount, setAmount] = useState<string>("");

  const delegateMutation = useDelegateMutation();
  const undelegateMutation = useUndelegateMutation();

  return (
    <div className={twMerge("rounded-xl bg-white p-5 shadow-xl", className)}>
      <h2 className="mb-4 text-xl font-semibold">Staking Terminal</h2>

      {/* Tabs */}
      <div className="mb-4 flex space-x-2">
        {tabs.map((tab) => (
          <button
            className={twMerge(
              "text-pickledBluewood h-8 flex-1 rounded-lg border border-transparent text-center text-xs transition-all",
              activeTab === tab
                ? "border-fadedOrange bg-fadedOrange/15 border"
                : "bg-desertStorm"
            )}
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              setAmount("");
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      <Input
        disabled={!validator}
        onChange={setAmount}
        onMaxClick={(amount) => {
          console.log(amount);
          setAmount(amount);
        }}
        value={amount}
      />

      {/* Validator Selection */}
      <SelectValidator
        className="mt-4"
        name={validator?.description?.moniker}
        onSelect={onSelect}
      />

      <div className="mt-1.5 flex justify-between">
        <div className="text-[11px]">
          My stake:{" "}
          <span
            className={twMerge(
              "from-sunriseOrange to-fadedOrange bg-linear-to-tr bg-clip-text text-transparent"
            )}
          >
            {formattedBalance} GNET
          </span>
        </div>
        <div className="text-[11px]">
          Comission:{" "}
          <span
            className={twMerge(
              "from-sunriseOrange to-fadedOrange bg-linear-to-tr bg-clip-text text-transparent"
            )}
          >
            {commission}%
          </span>
        </div>
      </div>
      <Button
        className="mt-4 h-10 w-full uppercase"
        disabled={!amount || new Decimal(amount).lte(0)}
        isLoading={delegateMutation.isPending}
        onClick={() => {
          if (!validator?.operator_address) {
            catchError(new Error("operator_address is undefined"));
            return;
          }

          if (activeTab === "Stake") {
            delegateMutation.mutate({
              amount,
              validatorAddress: validator.operator_address,
            });
          }
          if (activeTab === "Unstake") {
            undelegateMutation.mutate({
              amount,
              validatorAddress: validator.operator_address,
            });
          }
          if (activeTab === "Claim") {
            //
          }
        }}
      >
        {activeTab}
      </Button>
    </div>
  );
}
